// Capability checks for the account hierarchy.
//
// Levels (lowest rank number = most power):
//   owner : unique; can do anything, including transferring ownership.
//   l1    : full edit and view; cannot transfer ownership.
//   l2    : edit and view everything except users at or above their level.
//   l3    : edit officer status codes only; no audit log; otherwise view.
//   l4    : view everything except the audit log.
import type { User, UserLevel } from "./types";

const rank: Record<UserLevel, number> = {
  owner: 0,
  l1: 1,
  l2: 2,
  l3: 3,
  l4: 4,
};

export function levelRank(l: UserLevel): number { return rank[l]; }

export const LEVEL_LABEL: Record<UserLevel, string> = {
  owner: "Owner",
  l1: "Level 1",
  l2: "Level 2",
  l3: "Level 3",
  l4: "Level 4",
};

// The single platform-operator email — the human who builds and
// runs ShiftManager itself. Distinct from "agency owner": every
// paying agency has an Owner of their own tenant, but only one
// account anywhere is the platform developer. Used to swap "Owner"
// for "Developer" in audit / activity surfaces on this one account
// so the agency reads see "Owner Smith" (correct) instead of
// "Developer Smith" (false — Smith doesn't build the app).
export const PLATFORM_DEVELOPER_EMAIL = "jamesleonbond@gmail.com";

export function isPlatformDeveloper(
  u: { email?: string | null } | null | undefined,
): boolean {
  return !!u?.email && u.email.toLowerCase() === PLATFORM_DEVELOPER_EMAIL;
}

// True when the user should see beta-gated features. That's either
// the platform developer themselves, or a user the developer has
// tagged as a beta tester (profile.is_beta_tester = true). Everyone
// else sees only shipped features. Use inside feature gates:
//
//   if (canSeeBeta(me)) { renderNewThing() }
export function canSeeBeta(
  u: { email?: string | null; isBetaTester?: boolean } | null | undefined,
): boolean {
  if (!u) return false;
  if (isPlatformDeveloper(u)) return true;
  return u.isBetaTester === true;
}

export const LEVEL_DESCRIPTION: Record<UserLevel, string> = {
  owner: "Full control, including ownership transfer.",
  l1: "Edit everything; cannot transfer ownership.",
  l2: "Edit everything except users at/above Level 2.",
  l3: "Edit officer status codes only; no audit log.",
  l4: "View only, no audit log.",
};

export const can = {
  // Audit log is for supervisors and up. L3 (cell editors) and
  // L4 (view-only) don't see it. Mirrors the DB-side RLS policy
  // "read audit l2+" (migration 0022) so the client and server
  // gate on the same rank threshold.
  viewAudit: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  editCells: (u: User | null | undefined) => !!u && u.level !== "l4",
  editRoster: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,                                    // owner/L1/L2
  // Mock-schedule (schedule_scenarios) feature — same bracket as
  // editRoster. L2 is further scoped per-scenario by the backend
  // RLS to only their home dept; this flag just controls whether
  // the Mock schedules sidebar entry appears at all.
  seeMockSchedules: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,                                    // owner/L1/L2
  // Force-override an in-form validation error on a schedule cell.
  // L2 and up (Owner, L1, L2 supervisors) can stamp a code into a
  // cell even when the app rejected the normal save — used for
  // real-world situations the validation can't foresee. Forced
  // cells render in bold red so it's obvious the guard was
  // overridden, and Revert override in the picker undoes it.
  forceCellOverride: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  // Edits to existing employee rows (rank, badge, contact, level
  // within hierarchy rules) are available to supervisor and up.
  editEmployees: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  // Creating brand-new employee rows (and deleting them) is
  // restricted to Owner and L1 — L2 is operational, not directorial.
  createEmployees: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 1,
  // Sending the invite email on an existing employee row is the
  // same power bracket as editing their details: supervisors and
  // up, but not L3/L4.
  sendInvites: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  editShifts: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  // Permanently deleting an archived shift cascades to every
  // assignment that ever pointed at it — destroys schedule
  // history. Restricted to Owner / L1, the same bracket that
  // already manages Settings.
  deleteShifts: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 1,
  editDepartments: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  editShiftCodes: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  editAnySettings: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,

  // User-management checks
  createUser: (me: User | null | undefined) => !!me && rank[me.level] <= 2,
  editUser: (me: User | null | undefined, target: User) => {
    if (!me) return false;
    if (me.id === target.id) return false;        // can't edit self; demote yourself via transfer
    return rank[me.level] < rank[target.level];   // strictly higher authority
  },
  deleteUser: (me: User | null | undefined, target: User) => {
    if (!me) return false;
    if (target.level === "owner") return false;   // owner only via transfer+delete flow
    return rank[me.level] < rank[target.level] && me.id !== target.id;
  },
  setUserLevel: (
    me: User | null | undefined,
    target: User,
    newLevel: UserLevel,
  ) => {
    if (!me) return false;
    if (newLevel === "owner") return false;       // ownership transfer is its own flow
    if (rank[me.level] >= rank[target.level]) return false; // target must be below me
    if (rank[newLevel] <= rank[me.level]) return false;     // new level must stay below me
    return true;
  },

  transferOwnership: (me: User | null | undefined) => !!me && me.level === "owner",

  // Settings page is visible to every signed-in user — the panels
  // inside are individually gated by level (see Settings.tsx). Any
  // user needs to reach Settings for browser notifications, iOS
  // design toggle, account deletion, etc.
  viewSettings: (u: User | null | undefined) => !!u,
  // Agency-configuration panels — the first-block agency card,
  // Backups, Departments, Shift codes, Request fields, Shift bid —
  // gated to L2 and up. L2 is the operational-supervisor bracket
  // and can adjust day-to-day agency settings.
  editAgencyConfig: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  // Director-level settings: pay-period anchors + markers, seniority
  // mode, subscription. Only Owner / L1 — these change the shape of
  // the whole agency's schedule or billing.
  editDirectorSettings: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 1,
  // Who can use the "Preview as <level>" spoofing dropdown in the
  // sidebar. Extended from Owner/L1 to include L2 — supervisors
  // often want to check what an officer sees. The dropdown itself
  // further filters to only show levels BELOW the current user's
  // rank (see canPreviewAsLevel below), so an L2 sees L3 + L4 as
  // options but not Owner / L1 / L2.
  previewAsOtherLevel: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 2,
  // For a given (current user, candidate target level), can the
  // current user preview as that level? Enforces the "lower ranks
  // only" rule so a supervisor can't spoof someone above them.
  canPreviewAsLevel: (
    u: User | null | undefined,
    target: UserLevel,
  ): boolean => {
    if (!u) return false;
    if (rank[u.level] > 2) return false;
    return rank[target] > rank[u.level];
  },

  // Move the shared schedule pay-period anchor. Every user in
  // the agency lands on the same page, so letting anyone scroll
  // forward would jerk the whole agency's view. Restrict to Owner
  // and L1 — the same bracket that manages Settings.
  setScheduleAnchor: (u: User | null | undefined) =>
    !!u && rank[u.level] <= 1,

  // ---- Employee-level changes (same hierarchy rules as users) ----
  setEmployeeLevel: (
    me: User | null | undefined,
    targetLevel: UserLevel,
    newLevel: UserLevel,
  ) => {
    if (!me) return false;
    if (rank[me.level] > 2) return false;             // L3/L4 can't change levels at all
    if (newLevel === "owner") return false;           // ownership transfer is separate
    if (rank[me.level] >= rank[targetLevel]) return false; // target must be below me
    if (rank[newLevel] < rank[me.level]) return false;     // can't promote above myself
    return true;
  },
};
